import React, { useState, useEffect, useCallback, useRef } from "react";
import { X, Building2, User, Clock, AlertCircle, Edit2 } from "lucide-react";
import { ProjectService } from "../services/projectService";
import { DiscrepancyService } from "../services/discrepancyService";
import { supabase } from "../lib/supabase";
import MultiSelectDropdown from "./MultiSelectDropdown";
import { buildStructuredEmail } from "../utils/emailHtmlFormatter";
import { Modal, ModalBody, ModalFooter, Button } from "./ui";
import DiscrepancyModal from "./Discrepancies/DiscrepancyModal";
import "./HoldProjectModal.css";
import "./ExportAndSend/ExportForm.css";

const HOLD_REASONS = [
  { value: "waiting_updated_pdfs", label: "Waiting on Updated PDFs" },
  { value: "waiting_updated_cad", label: "Waiting on Updated CAD Files" },
  {
    value: "waiting_discrepancy_clarification",
    label: "Waiting on Discrepancy Clarification",
  },
  { value: "client_requested_hold", label: "Client Requested Hold" },
  { value: "other", label: "Other" },
];

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const calculateDaysOnHold = (holdStartDate) => {
  if (!holdStartDate) return 0;
  const start = new Date(holdStartDate);
  const now = new Date();
  const diffTime = Math.abs(now - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

const OnHoldReminderModal = ({ project: initialProject, emailTracking, isOpen, onClose, onSuccess }) => {
  const [project, setProject] = useState(initialProject);
  const [sendEmail, setSendEmail] = useState(true);
  const [daysUntilNextReminder, setDaysUntilNextReminder] = useState(14);
  const [formData, setFormData] = useState({
    emailPreview: "",
    emailHtml: "",
    emailTo: [],
    emailCc: [],
    emailSubject: "",
    customMessage: "",
    selectedDiscrepancyIds: [],
  });

  const [clientContacts, setClientContacts] = useState([]);
  const [projectDiscrepancies, setProjectDiscrepancies] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loadingDiscrepancies, setLoadingDiscrepancies] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editingDiscrepancy, setEditingDiscrepancy] = useState(null);
  const [showEditDiscrepancyModal, setShowEditDiscrepancyModal] = useState(false);
  const htmlUpdateTimerRef = useRef(null);

  // Load data when modal opens
  useEffect(() => {
    if (isOpen && initialProject?.id) {
      // Get project contact ID immediately
      const projectContactId = initialProject?.contactId || initialProject?.contact_id;

      // Reset form data when modal opens, pre-populate with project contact if available
      setFormData({
        emailPreview: "",
        emailHtml: "",
        emailTo: projectContactId ? [projectContactId] : [],
        emailCc: [],
        emailSubject: "",
        customMessage: "",
        selectedDiscrepancyIds: [],
      });

      // Reload project to get latest hold_discrepancy_ids from database
      loadFreshProjectData(initialProject.id);
      loadDiscrepancies(initialProject.id);
      loadCurrentUser();

      // Load contacts if client ID is available
      const clientId = initialProject?.clientId || initialProject?.client_id;
      if (clientId) {
        loadContacts(clientId);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialProject]);

  const loadFreshProjectData = async (projectId) => {
    try {
      const { data, error} = await supabase
        .from('projects')
        .select(`
          *,
          client:client_id(id, name),
          contact:contact_id(id, name)
        `)
        .eq('id', projectId)
        .single();

      if (error) throw error;
      console.log("📥 Loaded fresh project data with hold_discrepancy_ids:", data.hold_discrepancy_ids);
      setProject(data);
    } catch (err) {
      console.error('Error loading fresh project data:', err);
      // Fallback to initial project if load fails
      setProject(initialProject);
    }
  };

  // Reset form when modal opens (only run once when modal opens, not when project data reloads)
  useEffect(() => {
    if (isOpen && initialProject) {
      // Set default email toggle and days
      setSendEmail(true);
      const defaultDays = emailTracking?.recurring_follow_up_days || 14;
      setDaysUntilNextReminder(defaultDays);

      // Initialize with existing hold_discrepancy_ids if available
      const existingDiscrepancyIds = initialProject?.hold_discrepancy_ids || [];

      // Auto-fill "To" field with project contact if available
      const contactId = initialProject?.contactId || initialProject?.contact_id;
      const defaultEmailTo = contactId ? [contactId] : [];

      setFormData({
        emailPreview: "",
        emailHtml: "",
        emailTo: defaultEmailTo,
        emailCc: [],
        emailSubject: "",
        customMessage: "",
        selectedDiscrepancyIds: existingDiscrepancyIds,
      });

      setError(null);
    }
  }, [isOpen, initialProject, emailTracking]);

  // Auto-generate email when email is enabled and we have required data
  useEffect(() => {
    if (sendEmail && project && currentUser && !formData.emailPreview) {
      console.log("OnHoldReminderModal: Auto-generating email...", {
        sendEmail,
        hasProject: !!project,
        contactsCount: clientContacts.length,
        hasCurrentUser: !!currentUser,
        hasEmailPreview: !!formData.emailPreview
      });
      const timer = setTimeout(() => {
        handlePrepareEmail();
      }, 200);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendEmail, project, currentUser, formData.emailPreview]);

  // Auto-regenerate email HTML when custom message changes (debounced)
  useEffect(() => {
    if (!sendEmail || !formData.emailPreview) return;

    if (htmlUpdateTimerRef.current) {
      clearTimeout(htmlUpdateTimerRef.current);
    }

    htmlUpdateTimerRef.current = setTimeout(() => {
      updateEmailHTML();
    }, 1000);

    return () => {
      if (htmlUpdateTimerRef.current) {
        clearTimeout(htmlUpdateTimerRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.customMessage, formData.emailPreview, formData.emailTo, clientContacts, daysUntilNextReminder, sendEmail]);

  // Auto-regenerate email when discrepancies or days change
  useEffect(() => {
    if (sendEmail && formData.emailPreview && project && clientContacts.length > 0 && currentUser) {
      console.log("OnHoldReminderModal: Discrepancies or days changed, regenerating email...");
      const timer = setTimeout(() => {
        handlePrepareEmail();
      }, 100);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.selectedDiscrepancyIds.length, daysUntilNextReminder]);

  const loadDiscrepancies = async (projectId) => {
    setLoadingDiscrepancies(true);
    try {
      const discrepancies = await DiscrepancyService.getDiscrepanciesByProjectId(projectId);
      setProjectDiscrepancies(discrepancies || []);
    } catch (err) {
      console.error('Error loading discrepancies:', err);
    } finally {
      setLoadingDiscrepancies(false);
    }
  };

  const loadContacts = async (clientId) => {
    if (!clientId) {
      clientId = project?.client_id || project?.clientId;
      if (!clientId) return;
    }

    setLoadingContacts(true);
    try {
      const contacts = await ProjectService.getContactsByClientId(clientId);
      setClientContacts(contacts || []);
    } catch (err) {
      console.error("Error loading contacts:", err);
      setClientContacts([]);
    } finally {
      setLoadingContacts(false);
    }
  };

  const loadCurrentUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (user) {
        const { data: userData, error: profileError } = await supabase
          .from('employees')
          .select('*')
          .eq('email', user.email)
          .single();

        if (!profileError && userData) {
          setCurrentUser(userData);
        }
      }
    } catch (err) {
      console.error("Error loading current user:", err);
    }
  };

  const updateEmailHTML = useCallback(() => {
    if (!formData.emailPreview) return;

    try {
      // Get contact first name for personalized greeting
      console.log("📝 updateEmailHTML - emailTo:", formData.emailTo, "clientContacts:", clientContacts.length);
      const primaryContact = clientContacts.find(contact =>
        formData.emailTo.length > 0 && contact.id === formData.emailTo[0]
      );
      console.log("📝 updateEmailHTML - primaryContact:", primaryContact);
      const contactName = primaryContact?.name || '';
      const contactFirstName = contactName.split(' ')[0] || contactName;
      console.log("📝 updateEmailHTML - contactFirstName:", contactFirstName);

      // Parse the email preview to extract template messages and hold notice
      const previewLines = formData.emailPreview.split('\n').filter(line => line.trim());

      // Extract greeting (first line with just name) - always use contactFirstName if available
      const greeting = contactFirstName ? `${contactFirstName},` : (previewLines.length > 0 && previewLines[0].includes(',') ? previewLines[0] : ',');
      console.log("📝 updateEmailHTML - greeting:", greeting);

      // Build template messages (everything before hold notice)
      const templateMessages = [];
      const holdNoticeMessages = [];
      let foundHoldNotice = false;

      for (const line of previewLines) {
        if (line.includes('still on hold') || line.includes('remains on hold')) {
          foundHoldNotice = true;
          holdNoticeMessages.push(line);
        } else if (foundHoldNotice) {
          holdNoticeMessages.push(line);
        } else if (!line.includes(',') || line.split(' ').length > 2) {
          // Skip greeting line
          templateMessages.push(line);
        }
      }

      // Generate HTML using buildStructuredEmail
      const htmlContent = buildStructuredEmail({
        greeting: greeting,
        templateMessages: templateMessages,
        discrepancies: [],
        holdNoticeMessages: holdNoticeMessages,
        customMessage: formData.customMessage || "",
        signature: currentUser?.email_signature_html || "",
      });

      setFormData(prev => ({
        ...prev,
        emailHtml: htmlContent,
      }));
    } catch (error) {
      console.error('Error formatting hold reminder email HTML:', error);
    }
  }, [formData.emailPreview, formData.customMessage, formData.emailTo, currentUser, clientContacts]);

  const handlePrepareEmail = async () => {
    console.log("OnHoldReminderModal: handlePrepareEmail called", { project, currentUser, clientContacts });

    if (!project) {
      console.error("OnHoldReminderModal: No project data");
      setError("Project data not available");
      return;
    }

    setSaving(true);
    try {
      const holdReason = project?.holdReason || project?.hold_reason;
      console.log("OnHoldReminderModal: Hold reason:", holdReason);

      if (!holdReason) {
        console.error("OnHoldReminderModal: No hold reason found", project);
        setError("Hold reason not found");
        setSaving(false);
        return;
      }

      console.log("OnHoldReminderModal: Fetching template for reason:", holdReason);
      const template = await ProjectService.getHoldReasonEmailTemplate(holdReason);
      console.log("OnHoldReminderModal: Template received:", template);

      if (template) {
        // Auto-populate To field with project contact if empty
        let emailTo = formData.emailTo;
        const contactId = project?.contactId || project?.contact_id;
        if (emailTo.length === 0 && contactId) {
          emailTo = [contactId];
        }

        // Get contact first name for personalized greeting
        // Try clientContacts first, fallback to project.contact if not loaded yet
        const primaryContact = clientContacts.find(c =>
          emailTo.length > 0 && c.id === emailTo[0]
        );
        const contactName = primaryContact?.name ||
                           project?.contact?.name ||
                           project?.contactName ||
                           "";
        const contactFirstName = contactName.split(" ")[0] || contactName;

        // Get project name for personalization
        const projectName = project?.projectName || project?.name || "This project";

        // Get human-readable hold reason label for subject line
        const holdReasonLabel = HOLD_REASONS.find((r) => r.value === holdReason)?.label || "Status Update";

        // Build natural sentence from template - replace "This project" and adjust tense
        const naturalSentence = template
          .replace('This project has been put on hold', `This is a reminder that ${projectName} is still on hold`)
          .replace('This project is being put on hold', `This is a reminder that ${projectName} is still on hold`)
          .replace(/\.$/, ''); // Remove trailing period

        // Build hold notice messages for REMINDER with natural flow
        const holdNoticeMessages = [
          `${naturalSentence}.`,
          `We will send another reminder in ${daysUntilNextReminder} days.`
        ];

        // Add hold notes as additional context if provided
        const holdNotes = project?.holdNotes || project?.hold_notes;
        if (holdNotes) {
          holdNoticeMessages.push('', `Additional context: ${holdNotes}`);
        }

        // Build discrepancies array with structured data
        const structuredDiscrepancies = [];
        if (formData.selectedDiscrepancyIds && formData.selectedDiscrepancyIds.length > 0) {
          const selectedDiscrepancies = projectDiscrepancies.filter((d) =>
            formData.selectedDiscrepancyIds.includes(d.id),
          );

          selectedDiscrepancies.forEach((discrepancy) => {
            const hasLoomLink = discrepancy.loomLink && discrepancy.loomLink.trim();
            const filesWithUrls = (discrepancy.markupAttachment || []).filter(file => {
              const fileUrl = file.url || file.file_url || file.publicUrl || file.public_url || file.direct_download_url || file.signed_url || '';
              return fileUrl && fileUrl.trim() && fileUrl.startsWith('http');
            });

            // Only add discrepancy if it has video or files
            if (hasLoomLink || filesWithUrls.length > 0) {
              structuredDiscrepancies.push({
                type: discrepancy.discrepancyType || 'Discrepancy',
                videoUrl: hasLoomLink ? discrepancy.loomLink : '',
                fileLinks: filesWithUrls.map(file => ({
                  url: file.url || file.file_url || file.publicUrl || file.public_url || file.direct_download_url || file.signed_url,
                  filename: file.filename || file.original_filename || file.name || 'Markup File'
                }))
              });
            }
          });
        }

        // Generate subject line (reuse holdReasonLabel from above)
        const emailSubject = `${projectName} - Hold Reminder: ${holdReasonLabel}`;

        // Build template messages for hold reminder context
        const reminderTemplateMessages = [];

        // Add intro only if there are discrepancies
        if (structuredDiscrepancies.length > 0) {
          reminderTemplateMessages.push("Please see the relevant discrepancy/clarification files below:");
        }

        // Generate email HTML using buildStructuredEmail
        const htmlContent = buildStructuredEmail({
          greeting: `${contactFirstName},`,
          templateMessages: reminderTemplateMessages,
          discrepancies: structuredDiscrepancies,
          holdNoticeMessages: holdNoticeMessages,
          customMessage: formData.customMessage || "",
          signature: currentUser?.email_signature_html || "",
        });

        // Also build plain text preview for storage (backwards compatibility)
        let emailPreview = `${contactFirstName},\n\n`;
        emailPreview += `${naturalSentence}.\n\n`;
        emailPreview += `We will send another reminder in ${daysUntilNextReminder} days.\n\n`;
        if (holdNotes) {
          emailPreview += `Additional Notes:\n${holdNotes}\n\n`;
        }

        // Update form data with both preview and HTML
        setFormData((prev) => ({
          ...prev,
          emailTo: emailTo,
          emailPreview: emailPreview,
          emailSubject: emailSubject,
          emailHtml: htmlContent,
        }));
      }
    } catch (err) {
      console.error("Error preparing email:", err);
      setError("Failed to load email template");
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);

    // Auto-save discrepancy IDs to database when they change
    if (field === "selectedDiscrepancyIds" && project?.id) {
      saveDiscrepancyIds(value);
    }
  };

  const saveDiscrepancyIds = async (discrepancyIds) => {
    try {
      console.log("💾 Attempting to save discrepancy IDs to project:", project.id, "IDs:", discrepancyIds);

      const { data, error } = await supabase
        .from("projects")
        .update({
          hold_discrepancy_ids: discrepancyIds || [],
        })
        .eq("id", project.id)
        .select();

      if (error) {
        console.error("❌ Database error saving discrepancy IDs:", error);
        throw error;
      }

      console.log("✅ Auto-saved discrepancy IDs successfully:", discrepancyIds);
      console.log("📊 Updated project data:", data);
    } catch (error) {
      console.error("❌ Error auto-saving discrepancy IDs:", error);
      setError("Failed to save discrepancy selection");
    }
  };

  // Handle saving edited discrepancy
  const handleSaveDiscrepancy = async (updatedDiscrepancy) => {
    try {
      if (!editingDiscrepancy?.id) {
        throw new Error("No discrepancy ID found for save");
      }

      // Update the discrepancy in the database
      await DiscrepancyService.updateDiscrepancy(
        editingDiscrepancy.id,
        updatedDiscrepancy
      );

      // Reload the project discrepancies to get the updated data
      await loadDiscrepancies(project.id);

      // Close the modal
      setShowEditDiscrepancyModal(false);
      setEditingDiscrepancy(null);
    } catch (error) {
      console.error("Error saving discrepancy:", error);
      throw error; // Let the modal handle the error
    }
  };

  // Handle deleting discrepancy
  const handleDeleteDiscrepancy = async (discrepancyId) => {
    try {
      // Delete the discrepancy
      await DiscrepancyService.deleteDiscrepancy(discrepancyId);

      // Remove from selected list if it was selected
      const updatedIds = formData.selectedDiscrepancyIds.filter(
        (id) => id !== discrepancyId
      );

      // Update form data if the deleted discrepancy was selected
      if (updatedIds.length !== formData.selectedDiscrepancyIds.length) {
        handleInputChange("selectedDiscrepancyIds", updatedIds);
      }

      // Reload the project discrepancies
      await loadDiscrepancies(project.id);

      // Close the modal
      setShowEditDiscrepancyModal(false);
      setEditingDiscrepancy(null);
    } catch (error) {
      console.error("Error deleting discrepancy:", error);
      throw error; // Let the modal handle the error
    }
  };

  const handleSubmit = async () => {
    if (!daysUntilNextReminder || daysUntilNextReminder < 1) {
      setError("Please enter a valid number of days (minimum 1)");
      return;
    }

    if (sendEmail && formData.emailTo.length === 0) {
      setError("Please select at least one email recipient");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Build full email data if sending
      const emailData = sendEmail ? {
        emailPreview: formData.emailPreview,
        emailHtml: formData.emailHtml,
        emailTo: formData.emailTo,
        emailCc: formData.emailCc,
        emailSubject: formData.emailSubject,
        customMessage: formData.customMessage,
        discrepancyIds: formData.selectedDiscrepancyIds,
        discrepancies: projectDiscrepancies.filter(d =>
          formData.selectedDiscrepancyIds.includes(d.id)
        ),
      } : null;

      await ProjectService.sendHoldReminder(project.id, {
        sendEmail: sendEmail,
        daysInterval: daysUntilNextReminder,
        emailData: emailData,
      });

      setTimeout(async () => {
        await onSuccess();
        onClose();
      }, 500);
    } catch (err) {
      console.error("Error sending hold reminder:", err);
      setError(err.message || "Failed to send hold reminder");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const projectName = project?.projectName || project?.name || "Project";
  const jobNumber = project?.jobNumber || project?.job_number;
  const revNumber = project?.revNumber || project?.rev_number;
  const clientName = project?.client?.name || project?.clientName || "N/A";
  const contactName = project?.contact?.name || project?.contactName || "N/A";
  const modelingStatus = project?.modelingStatus || project?.modeling_status || "N/A";
  const holdReasonLabel = HOLD_REASONS.find((r) => r.value === project?.hold_reason)?.label || "Status Update";
  const daysOnHold = calculateDaysOnHold(project?.holdStartDate || project?.hold_start_date);

  return (
    <Modal trackAs="OnHoldReminderModal" isOpen={isOpen} onClose={onClose} size="xlarge" showCloseButton={false}>
      {/* Custom Header */}
      <div style={{
        padding: 'var(--space-6)',
        background: 'var(--dark-elevated)',
        borderBottom: '1px solid var(--gray-700)',
        borderTopLeftRadius: '12px',
        borderTopRightRadius: '12px',
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--space-4)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', flex: 1, minWidth: 0 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-400)',
                marginBottom: 'var(--space-1)',
                fontWeight: 'var(--font-medium)',
              }}>
                {jobNumber && revNumber ? `${jobNumber}.${revNumber}` : (jobNumber || 'No Job Number')}
              </div>
              <h2 style={{
                margin: 0,
                fontSize: 'var(--text-2xl)',
                fontWeight: 'var(--font-bold)',
                color: 'var(--gray-50)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {projectName}
              </h2>
            </div>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              backgroundColor: 'rgba(251, 191, 36, 0.1)',
              border: '1px solid rgba(251, 191, 36, 0.3)',
              borderRadius: 'var(--radius-full)',
              flexShrink: 0,
            }}>
              <Clock size={16} style={{ color: '#fbbf24' }} />
              <span style={{
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-semibold)',
                color: '#fbbf24',
              }}>
                {daysOnHold} days on hold
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: 'var(--space-2)',
              backgroundColor: 'transparent',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              color: 'var(--gray-400)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all var(--transition-fast)',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--gray-700)';
              e.currentTarget.style.color = 'var(--gray-200)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'var(--gray-400)';
            }}
          >
            <X size={20} />
          </button>
        </div>
      </div>

      <ModalBody style={{ background: 'var(--dark-bg)', color: 'var(--gray-50)', overflow: 'auto' }}>
        {error && (
          <div style={{
            padding: 'var(--space-4)',
            backgroundColor: 'var(--error-bg)',
            border: '1px solid var(--error-border)',
            borderRadius: 'var(--radius-lg)',
            marginBottom: 'var(--space-6)',
            color: 'var(--error-text)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
          }}>
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        {/* Project Context Bar - Horizontal Layout */}
        <div className="export-form-section" style={{ marginBottom: 'var(--space-6)' }}>
              <h3 style={{ marginBottom: 'var(--space-4)' }}>Project Context</h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 'var(--space-3)',
                padding: 'var(--space-4)',
                backgroundColor: 'var(--dark-surface)',
                border: '1px solid var(--gray-700)',
                borderRadius: 'var(--radius-lg)',
              }}>
                {/* Client */}
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: 'var(--space-2)',
                    color: 'var(--gray-400)',
                    fontSize: 'var(--text-xs)',
                    fontWeight: 'var(--font-medium)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    <Building2 size={12} style={{ display: 'inline', marginRight: '4px' }} />
                    Client
                  </label>
                  <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-sm)', fontWeight: 600 }}>
                    {clientName}
                  </div>
                </div>

                {/* Contact */}
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: 'var(--space-2)',
                    color: 'var(--gray-400)',
                    fontSize: 'var(--text-xs)',
                    fontWeight: 'var(--font-medium)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    <User size={12} style={{ display: 'inline', marginRight: '4px' }} />
                    Contact
                  </label>
                  <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-sm)', fontWeight: 600 }}>
                    {contactName}
                  </div>
                </div>

                {/* Status */}
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: 'var(--space-2)',
                    color: 'var(--gray-400)',
                    fontSize: 'var(--text-xs)',
                    fontWeight: 'var(--font-medium)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Status
                  </label>
                  <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-sm)', fontWeight: 600 }}>
                    {modelingStatus}
                  </div>
                </div>

                {/* Hold Reason */}
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: 'var(--space-2)',
                    color: 'var(--gray-400)',
                    fontSize: 'var(--text-xs)',
                    fontWeight: 'var(--font-medium)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Hold Reason
                  </label>
                  <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-sm)', fontWeight: 600 }}>
                    {holdReasonLabel}
                  </div>
                </div>
              </div>
            </div>

            {/* Hold Information Section - Full Width */}
            <div className="export-form-section" style={{ marginBottom: 'var(--space-6)' }}>
              <h3 style={{ marginBottom: 'var(--space-4)' }}>Hold Information</h3>
              <div style={{
                padding: 'var(--space-4)',
                backgroundColor: 'var(--dark-surface)',
                border: '1px solid var(--gray-700)',
                borderRadius: 'var(--radius-lg)',
              }}>
                {/* Hold Notes (if exists) */}
                {project?.hold_notes && (
                  <div style={{ marginBottom: 'var(--space-3)' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: 'var(--space-2)',
                      color: 'var(--gray-400)',
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'var(--font-medium)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Notes
                    </label>
                    <div style={{
                      color: 'var(--gray-100)',
                      fontSize: 'var(--text-sm)',
                      lineHeight: '1.5',
                    }}>
                      {project.hold_notes}
                    </div>
                  </div>
                )}

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: 'var(--space-3)',
                }}>
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: 'var(--space-2)',
                      color: 'var(--gray-400)',
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'var(--font-medium)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Hold Start Date
                    </label>
                    <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-sm)' }}>
                      {formatDate(project?.holdStartDate || project?.hold_start_date)}
                    </div>
                  </div>

                  {emailTracking?.last_email_sent_date && (
                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: 'var(--space-2)',
                        color: 'var(--gray-400)',
                        fontSize: 'var(--text-xs)',
                        fontWeight: 'var(--font-medium)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Last Email Sent
                      </label>
                      <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-sm)' }}>
                        {formatDate(emailTracking.last_email_sent_date)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Reminder Configuration Section - Full Width */}
            <div className="export-form-section" style={{ marginBottom: 'var(--space-6)' }}>
              <h3 style={{ marginBottom: 'var(--space-4)' }}>Reminder Configuration</h3>
              <div style={{
                padding: 'var(--space-4)',
                backgroundColor: 'var(--dark-surface)',
                border: '1px solid var(--gray-700)',
                borderRadius: 'var(--radius-lg)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-4)',
              }}>
                {/* Send Email Toggle */}
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: 'var(--space-2)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-medium)',
                    color: 'var(--gray-300)',
                  }}>
                    Send Reminder Email
                  </label>
                  <div style={{
                    display: 'inline-flex',
                    padding: '2px',
                    backgroundColor: 'var(--dark-elevated)',
                    border: '1px solid var(--gray-700)',
                    borderRadius: 'var(--radius-md)',
                    width: 'fit-content',
                  }}>
                    <button
                      type="button"
                      onClick={() => setSendEmail(true)}
                      style={{
                        padding: '6px 20px',
                        border: 'none',
                        borderRadius: 'calc(var(--radius-md) - 2px)',
                        backgroundColor: sendEmail ? 'var(--cta-blue)' : 'transparent',
                        color: sendEmail ? 'white' : 'var(--gray-400)',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-medium)',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      onClick={() => setSendEmail(false)}
                      style={{
                        padding: '6px 20px',
                        border: 'none',
                        borderRadius: 'calc(var(--radius-md) - 2px)',
                        backgroundColor: !sendEmail ? 'var(--cta-blue)' : 'transparent',
                        color: !sendEmail ? 'white' : 'var(--gray-400)',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-medium)',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      No
                    </button>
                  </div>
                </div>

                {/* Days Until Next Reminder */}
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: 'var(--space-2)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-medium)',
                    color: 'var(--gray-300)',
                  }}>
                    Days Until Next Reminder <span style={{ color: 'var(--danger)' }}>*</span>
                  </label>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    padding: 'var(--space-4)',
                    backgroundColor: 'var(--dark-elevated)',
                    border: '1px solid var(--gray-700)',
                    borderRadius: 'var(--radius-md)',
                  }}>
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-400)' }}>
                      Send next reminder in
                    </span>
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={daysUntilNextReminder}
                      onChange={(e) => setDaysUntilNextReminder(parseInt(e.target.value) || 1)}
                      style={{
                        width: '80px',
                        padding: 'var(--space-2)',
                        backgroundColor: 'var(--dark-bg)',
                        border: '1px solid var(--gray-600)',
                        borderRadius: 'var(--radius-md)',
                        color: 'var(--gray-200)',
                        fontSize: 'var(--text-sm)',
                        fontFamily: 'inherit',
                        textAlign: 'center',
                        transition: 'border-color var(--transition-fast)',
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = 'var(--cta-blue)'}
                      onBlur={(e) => e.currentTarget.style.borderColor = 'var(--gray-600)'}
                    />
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-400)' }}>
                      days
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Discrepancy Selection - Only show if sendEmail=true */}
            {sendEmail && (
              <div className="export-form-section" style={{ marginBottom: 'var(--space-6)' }}>
                <h3 style={{
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)',
                  marginBottom: 'var(--space-4)',
                }}>
                  Related Discrepancies
                </h3>

                {loadingDiscrepancies ? (
                  <div style={{
                    padding: 'var(--space-4)',
                    textAlign: 'center',
                    color: 'var(--gray-400)',
                  }}>
                    Loading discrepancies...
                  </div>
                ) : projectDiscrepancies.length === 0 ? (
                  <div style={{
                    padding: 'var(--space-4)',
                    textAlign: 'center',
                    color: 'var(--gray-400)',
                  }}>
                    No discrepancies found for this project
                  </div>
                ) : (
                  <div className="export-discrepancies-list">
                    {projectDiscrepancies.map((discrepancy) => {
                      const isSelected = formData.selectedDiscrepancyIds?.includes(discrepancy.id);
                      return (
                        <div
                          key={discrepancy.id}
                          className={`export-discrepancy-item ${isSelected ? 'selected' : ''}`}
                          onClick={() => {
                            const currentIds = formData.selectedDiscrepancyIds || [];
                            const newIds = isSelected
                              ? currentIds.filter((id) => id !== discrepancy.id)
                              : [...currentIds, discrepancy.id];
                            handleInputChange("selectedDiscrepancyIds", newIds);
                          }}
                        >
                          <div className="discrepancy-checkbox">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              readOnly
                            />
                          </div>
                          <div className="discrepancy-info">
                            <span className="discrepancy-type">
                              {discrepancy.discrepancyType || discrepancy.type}
                            </span>
                            <span className="discrepancy-separator">•</span>
                            <span className="discrepancy-created">
                              {new Date(discrepancy.createdAt || discrepancy.created_at).toLocaleDateString()}
                            </span>
                            <span className="discrepancy-label">by</span>
                            <span className="discrepancy-creator">
                              {discrepancy.createdByName || discrepancy.created_by_name || 'Unknown'}
                            </span>
                            <span className="discrepancy-separator">•</span>
                            <span className={`discrepancy-status status-${(discrepancy.discrepancyStatus || discrepancy.status || '').toLowerCase().replace(/\s+/g, '-')}`}>
                              {discrepancy.discrepancyStatus || discrepancy.status}
                            </span>
                            {(discrepancy.discrepancyStatus === 'Sent' || discrepancy.status === 'Sent') && (discrepancy.sentDate || discrepancy.sent_date) && (
                              <>
                                <span className="discrepancy-separator">•</span>
                                <span className="discrepancy-label">Sent:</span>
                                <span className="discrepancy-sent-date">
                                  {new Date(discrepancy.sentDate || discrepancy.sent_date).toLocaleDateString()}
                                </span>
                              </>
                            )}
                          </div>
                          <button
                            className="discrepancy-edit-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingDiscrepancy(discrepancy);
                              setShowEditDiscrepancyModal(true);
                            }}
                            title="Edit discrepancy"
                          >
                            <Edit2 size={16} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Email Recipients and Composition - Only show if email enabled */}
            {sendEmail && (
              <>
                {/* Email Recipients Section */}
                <div className="export-form-section">
                  <h3>Email Recipients</h3>
                  <div className="export-recipients-section">
                    <p style={{
                      fontSize: 'var(--text-sm)',
                      color: 'var(--gray-400)',
                      margin: '0 0 var(--space-4) 0'
                    }}>
                      Select contacts for hold reminder email
                    </p>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
                      {/* Left: To and CC Fields Stacked */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                        {/* Email To Field */}
                        <div className="export-email-field">
                          <label className="export-email-field-label">
                            To: <span className="required">*</span>
                          </label>
                          <div className="export-email-field-input">
                            <MultiSelectDropdown
                              options={clientContacts.map(c => c.name + (c.email ? ` (${c.email})` : ""))}
                              selected={(() => {
                                const selectedNames = (formData.emailTo || [])
                                  .map((id) => {
                                    const c = clientContacts.find(contact => contact.id === id);
                                    const result = c ? c.name + (c.email ? ` (${c.email})` : "") : "";
                                    console.log(`📧 Selected prop - mapping ID ${id} to:`, result, "contact:", c);
                                    return result;
                                  })
                                  .filter(Boolean);
                                console.log("📧 Selected prop final value:", selectedNames);
                                return selectedNames;
                              })()}
                              onChange={(selectedNames) => {
                                console.log("📧 To field onChange - selectedNames:", selectedNames);
                                console.log("📧 Available clientContacts:", clientContacts);
                                const selectedIds = selectedNames
                                  .map((name) => {
                                    const c = clientContacts.find(contact => contact.name + (contact.email ? ` (${contact.email})` : "") === name);
                                    console.log(`📧 Mapping "${name}" to contact:`, c, "-> ID:", c?.id);
                                    return c ? c.id : null;
                                  })
                                  .filter(Boolean);
                                console.log("📧 Final selectedIds:", selectedIds);
                                handleInputChange("emailTo", selectedIds);
                              }}
                              placeholder="Select email recipients..."
                              disabled={loadingContacts}
                            />
                          </div>
                        </div>

                        {/* Email CC Field */}
                        <div className="export-email-field">
                          <label className="export-email-field-label">CC:</label>
                          <div className="export-email-field-input">
                            <MultiSelectDropdown
                              options={clientContacts
                                .filter(c => !formData.emailTo.includes(c.id))
                                .map(c => c.name + (c.email ? ` (${c.email})` : ""))}
                              selected={(formData.emailCc || [])
                                .map((id) => {
                                  const c = clientContacts.find(contact => contact.id === id);
                                  return c ? c.name + (c.email ? ` (${c.email})` : "") : "";
                                })
                                .filter(Boolean)}
                              onChange={(selectedNames) => {
                                const selectedIds = selectedNames
                                  .map((name) => {
                                    const c = clientContacts.find(contact => contact.name + (contact.email ? ` (${contact.email})` : "") === name);
                                    return c ? c.id : null;
                                  })
                                  .filter(Boolean);
                                handleInputChange("emailCc", selectedIds);
                              }}
                              placeholder="Add CC recipients (optional)..."
                              disabled={loadingContacts}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Right: Empty space for symmetry */}
                      <div></div>
                    </div>
                  </div>
                </div>

                {/* Email Content Section */}
                <div className="export-form-section">
                  <h3 style={{ marginBottom: 'var(--space-4)' }}>Email Content</h3>

                  {/* Subject Field */}
                  <div style={{ marginBottom: 'var(--space-4)' }}>
                    <label htmlFor="emailSubject" style={{
                      display: 'block',
                      marginBottom: 'var(--space-2)',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-medium)',
                      color: 'var(--gray-300)',
                    }}>
                      Subject
                    </label>
                    <input
                      type="text"
                      id="emailSubject"
                      value={formData.emailSubject}
                      onChange={(e) => handleInputChange("emailSubject", e.target.value)}
                      placeholder="Email subject..."
                      style={{
                        width: '100%',
                        padding: 'var(--space-3)',
                        backgroundColor: 'var(--dark-elevated)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-md)',
                        color: 'var(--gray-200)',
                        fontSize: 'var(--text-sm)',
                        fontFamily: 'inherit',
                        transition: 'border-color var(--transition-fast)',
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = 'var(--cta-blue)'}
                      onBlur={(e) => e.currentTarget.style.borderColor = 'var(--gray-700)'}
                    />
                  </div>

                  {/* Two-column layout: Custom Message + Preview */}
                  <div className="export-email-container">
                    {/* Left Side - Email Editor with Custom Message */}
                    <div className="export-email-editor">
                      <div className="export-email-editor-header">
                        <h4>Custom Message (Optional)</h4>
                        <small>Add additional notes to the auto-generated email</small>
                      </div>
                      <textarea
                        id="customMessage"
                        name="customMessage"
                        value={formData.customMessage}
                        onChange={(e) => handleInputChange("customMessage", e.target.value)}
                        placeholder="Add any custom notes here (optional)..."
                        className="export-email-body"
                      />
                    </div>

                    {/* Right Side - HTML Preview */}
                    <div className="export-email-preview">
                      <div className="export-email-preview-header">
                        <h4>Email Preview</h4>
                        <small>How the email will appear</small>
                      </div>
                      <div className="export-email-preview-content">
                        <iframe
                          srcDoc={formData.emailHtml || '<div style="padding: 20px; text-align: center; color: #666;">Email preview will appear here</div>'}
                          title="Email Preview"
                          className="export-email-preview-iframe"
                          sandbox="allow-same-origin allow-scripts"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
      </ModalBody>

      <ModalFooter style={{ background: 'var(--dark-surface)', borderTop: '1px solid var(--gray-700)', borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-3)', width: '100%' }}>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={saving || !daysUntilNextReminder}
          >
            {saving ? 'Processing...' : (sendEmail ? 'Send Reminder' : 'Snooze Reminder')}
          </Button>
        </div>
      </ModalFooter>

      {/* Discrepancy Edit Modal */}
      {showEditDiscrepancyModal && (
        <DiscrepancyModal
          discrepancy={editingDiscrepancy}
          isOpen={showEditDiscrepancyModal}
          isCreating={false}
          onClose={() => {
            setShowEditDiscrepancyModal(false);
            setEditingDiscrepancy(null);
          }}
          onSave={handleSaveDiscrepancy}
          onDelete={handleDeleteDiscrepancy}
          zIndex={10001}
        />
      )}
    </Modal>
  );
};

export default OnHoldReminderModal;
